import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
// components
import DatePicker from '../../../components/DatePicker';
import SelectDropdown from '../../../components/SelectDropdown';
import Table from '../../../components/Table';
import ErrorText from '../../../components/ErrorText';
import NoData from '../../../components/NoData';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';
import { ToggleComponent } from '../../../components/Toggle';
import ReportDownloadComponent from '../../../components/DownloadReport/ReportDownloadComponent';
import { HelperNote } from '../../../components/HelperNote';
// hooks
import { useTableDataHook } from '../hooks/useTableDataHook';
// constants
import {
  CHANNEL_OPPS,
  SUB_BRANCH_DASH_OPPS_BY_CHANNEL,
} from '../../../constants/datePickerConstants';
import {
  DOWNLOAD_CHANNEL_OPPPS_LOCAL,
  DOWNLOAD_CHANNEL_OPPS_STORE,
  SEM_OPPS_DOWNLOAD_FILE_NAME,
  TOTAL,
  YOY_TOTAL,
  SUB_BRANCH_SEM_OPPS_DOWNLOAD_FILE_NAME,
  OPPS_SPEND_CHANNEL_VENDOR_SEM_WITH_STORE,
  LOCAL_WEBSITE,
  OTHER,
} from '../constants';
import {
  ENABLE_LOCAL_SPEND,
  getSettingValue,
  isStoreValid,
  LOCAL_BRANCH_SPEND,
  LocalBranchToogleHelperNoteOBJ,
} from '../../../constants/utils';
// services functions
import { getColorStyleForCell } from '../../../utils/colorSchmeOverallPerformance';
import {
  getChannelOppsDownloadFilename,
  getTransformedHeaderArray,
  percentValueForPallete,
  transformHeaderArray,
  transformYOYChannelHeader,
  transformYOYCPOChannelHeader,
} from '../services/ChannelOppsServices';

const OpportunitieByChannel = ({ subBranchDashboard = false }) => {
  // Custom Hook Call for data
  const {
    options,
    defaultFilter,
    resetStore,
    handleResetForStore,
    handleStoreIdChange,
    channelOppsRowdata,
    isLoading,
    error,
    spendToggleState,
    setSpendToggleState,
    storeId,
    showSpendToggle,
    userData,
    yoyChannelRowData,
    yoyChannelLoading,
    yoyChannelError,
    initialLoading,
    setIsReportDownloading,
    isReportDownloading,
    timePeriodFilter,
    semOppsData,
    semOppsDataLoading,
    semOppsDataError,
    isSemOppsReportDownloading,
    setIsSemOppsReportDownloading,
  } = useTableDataHook({ subBranchDashboard });

  const semOppsTableHeadingUI = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6" align="left">
        SEM Opps Table
      </Typography>
      <ReportDownloadComponent
        component={OPPS_SPEND_CHANNEL_VENDOR_SEM_WITH_STORE}
        appliedFilters={timePeriodFilter}
        filename={
          subBranchDashboard ? SUB_BRANCH_SEM_OPPS_DOWNLOAD_FILE_NAME : SEM_OPPS_DOWNLOAD_FILE_NAME
        }
        setIsDownloading={setIsSemOppsReportDownloading}
        isDownloading={isSemOppsReportDownloading}
        disabled={semOppsDataLoading || semOppsDataError || !semOppsData?.length}
      />
    </div>
  );

  const renderChannelOppsTable = () => {
    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
          }}
        >
          <NoData textValue="Error loading data" />
        </Box>
      );
    }

    if (isLoading) {
      return (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            margiLeft: '64px',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      );
    }

    return (
      <div style={{ width: '90%', marginLeft: '64px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" align="left">
            Channel Opps Table
          </Typography>
          <ReportDownloadComponent
            component={
              spendToggleState ? DOWNLOAD_CHANNEL_OPPPS_LOCAL : DOWNLOAD_CHANNEL_OPPS_STORE
            }
            appliedFilters={timePeriodFilter}
            filename={getChannelOppsDownloadFilename(spendToggleState, subBranchDashboard)}
            setIsDownloading={setIsReportDownloading}
            isDownloading={isReportDownloading}
            disabled={isLoading || error || !channelOppsRowdata?.length}
          />
        </div>
        {channelOppsRowdata?.length > 0 ? (
          <Table
            boldHeaders={true}
            boldDataColumns={['Channel_Name']}
            stickyColumns={['Channel_Name']}
            data={channelOppsRowdata}
            columns={transformHeaderArray}
            applyStyleToLastRow
            getCellProps={(cellInfo) => {
              if (
                ['Close_Rate_Gross', 'Average_Job_Size'].includes(cellInfo.column.id) &&
                cellInfo.value
              ) {
                return {
                  style: {
                    ...getColorStyleForCell(
                      cellInfo.column.id,
                      percentValueForPallete(
                        channelOppsRowdata,
                        cellInfo.column.id,
                        cellInfo.value
                      ),
                      [TOTAL, LOCAL_WEBSITE, OTHER].includes(cellInfo?.row?.original?.Channel_Name)
                    ),
                  },
                };
              }
              return { style: {} };
            }}
            applySort
            excludedRowNameForSort="Total"
          />
        ) : (
          <NoData />
        )}
      </div>
    );
  };

  const renderSemOppsTable = () => {
    if (semOppsDataError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '400px',
            marginLeft: '64px',
            width: '90%',
          }}
        >
          {semOppsTableHeadingUI()}
          <NoData textValue="Error loading data" />
        </Box>
      );
    }

    if (semOppsDataLoading) {
      return (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            margiLeft: '64px',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      );
    }

    return (
      <div style={{ width: '90%', marginLeft: '64px' }}>
        {semOppsTableHeadingUI()}
        {semOppsData?.length > 0 ? (
          <>
            <Table
              boldHeaders={true}
              boldDataColumns={['Channel_Name', 'Vendor']}
              stickyColumns={['Channel_Name', 'Vendor']}
              data={semOppsData}
              columns={getTransformedHeaderArray()}
              applyStyleToLastRow
              getCellProps={(cellInfo) => {
                if (
                  ['Close_Rate_Gross', 'Average_Job_Size'].includes(cellInfo.column.id) &&
                  cellInfo.value
                ) {
                  return {
                    style: {
                      ...getColorStyleForCell(
                        cellInfo.column.id,
                        percentValueForPallete(semOppsData, cellInfo.column.id, cellInfo.value),
                        [TOTAL, LOCAL_WEBSITE, OTHER].includes(
                          cellInfo?.row?.original?.Channel_Name
                        )
                      ),
                    },
                  };
                }
                return { style: {} };
              }}
              applySort
              excludedRowNameForSort="Total"
            />
            <HelperNote note={LocalBranchToogleHelperNoteOBJ.semOppsTable} />
          </>
        ) : (
          <NoData />
        )}
      </div>
    );
  };

  return options?.length > 0 ? (
    <div style={{ height: '88vh', overflow: 'scroll', padding: '20px', paddingBottom: '100px' }}>
      <div style={{ width: 'fitContent', marginBottom: '20px' }}>
        <div
          style={{
            width: '90%',
            textAlign: 'left',
            marginLeft: '64px',
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            {subBranchDashboard ? 'Opps by Channel Subbranches' : 'Opportunities by Channel'}
          </span>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {showSpendToggle && getSettingValue(userData?.data?.settings, ENABLE_LOCAL_SPEND) && (
              <ToggleComponent
                setState={setSpendToggleState}
                currentState={spendToggleState}
                disabled={isLoading}
                toggleText={LOCAL_BRANCH_SPEND}
                hideToggle={isStoreValid(storeId, options)}
                subBranchDashboard={subBranchDashboard}
              />
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: '13px',
          width: '90%',
          marginLeft: '64px',
        }}
      >
        <div style={{ marginRight: 'auto' }}>
          <SelectDropdown
            options={options}
            label={'Select Branch'}
            resetValues={resetStore}
            handleResetValues={handleResetForStore}
            singleSelectDefaultValue={options[0]?.value}
            multiSelectDefaultValues={subBranchDashboard ? options.map((opt) => opt.value) : []}
            width={'200px'}
            handleFilterChange={handleStoreIdChange}
            marginExist={false}
            disabled={isLoading}
            multiple={subBranchDashboard}
            isSelectAll={subBranchDashboard}
            allOptionsSelectedText="All Branches"
          />
        </div>
        <DatePicker
          setFilter={defaultFilter}
          component={subBranchDashboard ? SUB_BRANCH_DASH_OPPS_BY_CHANNEL : CHANNEL_OPPS}
          combineCompare={false}
        />
      </div>
      {/* Table One-> Channel Opps*/}
      {renderChannelOppsTable()}
      {/* Table Two-> SEM Opps*/}
      {renderSemOppsTable()}

      {!subBranchDashboard && (
        <div style={{ width: '60%', marginLeft: '64px' }}>
          <Typography variant="h6" align="left">
            YTD Performance ROMI
          </Typography>
          {yoyChannelRowData?.length > 0 && !yoyChannelLoading && !yoyChannelError && (
            <div>
              <Table
                boldHeaders={true}
                boldDataColumns={['Channel_Name']}
                stickyColumns={['Channel_Name']}
                data={yoyChannelRowData}
                columns={transformYOYChannelHeader(userData?.data?.current_year)}
                applyStyleToLastRow
                applySort
                excludedRowNameForSort={YOY_TOTAL}
                initialSortBy={['spend_current']}
              />
              <HelperNote note={LocalBranchToogleHelperNoteOBJ.romiTable} />
            </div>
          )}
          {yoyChannelError && (
            <ErrorText reason="Error loading data for YTD Performance ROMI. Please try again." />
          )}
          {!yoyChannelError &&
            !yoyChannelLoading &&
            yoyChannelRowData?.length === 0 &&
            !initialLoading.current && <NoData />}
          {yoyChannelLoading && (
            <ShimmerComponent
              updatedStyle={{
                width: '90%',
                height: '100%',
                backgroundSize: '2000px 800px',
                border: '0px',
                margin: 'auto',
                padding: '0 20px',
              }}
            >
              <BouncingLogo height="72vh" text="Loading..." />
            </ShimmerComponent>
          )}
        </div>
      )}

      {!subBranchDashboard && (
        <div style={{ width: '60%', marginLeft: '64px' }}>
          <Typography variant="h6" align="left">
            YTD Performance CPO
          </Typography>
          {yoyChannelRowData?.length > 0 && !yoyChannelLoading && !yoyChannelError && (
            <div>
              <Table
                boldHeaders={true}
                boldDataColumns={['Channel_Name']}
                stickyColumns={['Channel_Name']}
                data={yoyChannelRowData}
                columns={transformYOYCPOChannelHeader(userData?.data?.current_year)}
                applyStyleToLastRow
                applySort
                excludedRowNameForSort={YOY_TOTAL}
                initialSortBy={['spend_current']}
              />
              <HelperNote note={LocalBranchToogleHelperNoteOBJ.cpoTable} />
            </div>
          )}
          {yoyChannelError && (
            <ErrorText reason="Error loading data for YTD Performance CPO. Please try again." />
          )}
          {!yoyChannelError &&
            !yoyChannelLoading &&
            yoyChannelRowData?.length === 0 &&
            !initialLoading.current && <NoData />}
          {yoyChannelLoading && (
            <ShimmerComponent
              updatedStyle={{
                width: '90%',
                height: '100%',
                backgroundSize: '2000px 800px',
                border: '0px',
                margin: 'auto',
                padding: '0 20px',
              }}
            >
              <BouncingLogo height="72vh" text="Loading..." />
            </ShimmerComponent>
          )}
        </div>
      )}
    </div>
  ) : (
    <div style={{ height: '88vh', overflow: 'scroll', padding: '20px', paddingBottom: '100px' }}>
      <div style={{ width: 'fitContent', marginBottom: '55px' }}>
        <div
          style={{
            width: '90%',
            textAlign: 'left',
            margin: 'auto',
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.6',
          }}
        >
          <ErrorText reason="No branches assigned to user" />
        </div>
      </div>
    </div>
  );
};

OpportunitieByChannel.propTypes = {
  subBranchDashboard: PropTypes.bool,
};

export default OpportunitieByChannel;
